/**
 * QuoteData English/Spanish field accessors for the dual-key transition.
 * Prefer English; fall back to deprecated Spanish aliases.
 */
import type { QuoteConsignee, QuoteData, QuoteProduct, QuoteResults, QuoteSupplier } from '../../types';

/** Top-level quote row bilingual pairs (DB English ← legacy Spanish). */
export const QUOTE_BILINGUAL_PAIRS: [string, string][] = [
  ['date', 'fecha'],
  ['client', 'cliente'],
  ['salesRep', 'comercial'],
  ['operator', 'operador'],
  ['pieces', 'piezas'],
  ['weightKg', 'pesoKg'],
  ['dimensions', 'dimensiones'],
  ['freightCost', 'costoFlete'],
  ['totalToBill', 'totalAFacturar'],
  ['notes', 'observaciones'],
  ['consecutive', 'consecutivo'],
  ['operationType', 'tipoOperacion'],
];

type QuoteSubRow = Record<string, unknown>;

/** Sync EN/ES keys inside consignee/supplier/product/results JSON blobs (in-place). */
export function normalizeQuoteSubfields(
  destinatario: QuoteSubRow | null | undefined,
  proveedor: QuoteSubRow | null | undefined,
  producto: QuoteSubRow | null | undefined,
  resultados: QuoteSubRow | null | undefined,
): void {
  if (destinatario) {
    destinatario.businessName ??= destinatario.company ?? destinatario.razonSocial;
    destinatario.company ??= destinatario.businessName;
    destinatario.razonSocial ??= destinatario.businessName;
    destinatario.contacto ??= destinatario.name; destinatario.name ??= destinatario.contacto;
    destinatario.direccionEntrega ??= destinatario.address; destinatario.address ??= destinatario.direccionEntrega;
    destinatario.ciudad ??= destinatario.city; destinatario.city ??= destinatario.ciudad;
    destinatario.pais ??= destinatario.country; destinatario.country ??= destinatario.pais;
    destinatario.codigoPostal ??= destinatario.postalCode; destinatario.postalCode ??= destinatario.codigoPostal;
  }
  if (proveedor) {
    proveedor.nombreCompania ??= proveedor.company; proveedor.company ??= proveedor.nombreCompania;
    proveedor.personaContacto ??= proveedor.name; proveedor.name ??= proveedor.personaContacto;
    proveedor.direccion ??= proveedor.address; proveedor.address ??= proveedor.direccion;
    proveedor.ciudad ??= proveedor.city; proveedor.city ??= proveedor.ciudad;
    proveedor.pais ??= proveedor.country; proveedor.country ??= proveedor.pais;
    proveedor.zipCode ??= proveedor.postalCode; proveedor.postalCode ??= proveedor.zipCode;
  }
  if (producto) {
    // Product: English wins. The quote forms only write the English keys, and nothing on the
    // server writes the product, so a Spanish value that disagrees is a stale mirror (a quote
    // edited from 1 to 3 pieces kept `cantidadPiezas: 1` and the client saw 1 piece). Not
    // applied to `resultados`: the server writes `estadoAprobacion` & co. only in Spanish.
    const preferEnglish = (en: string, es: string) => {
      producto[en] = producto[en] ?? producto[es];
      producto[es] = producto[en];
    };
    preferEnglish('name', 'nombre');
    preferEnglish('packagingType', 'tipoEmpaque');
    preferEnglish('declaredValue', 'valorDeclarado');
    preferEnglish('pieceCount', 'cantidadPiezas');
    preferEnglish('weightsPerPiece', 'pesosPorPieza');
    preferEnglish('weightPerPiece', 'pesoPorPieza');
    preferEnglish('quoteWeight', 'pesoCotizacion');
  }
  if (resultados) {
    resultados.pesoTotalFisico ??= resultados.totalPhysicalWeight; resultados.totalPhysicalWeight ??= resultados.pesoTotalFisico;
    resultados.pesoTotalVolumetrico ??= resultados.totalVolumetricWeight; resultados.totalVolumetricWeight ??= resultados.pesoTotalVolumetrico;
    resultados.pesoFacturable ??= resultados.billableWeight; resultados.billableWeight ??= resultados.pesoFacturable;
    resultados.precioUSD ??= resultados.priceUSD; resultados.priceUSD ??= resultados.precioUSD;
    resultados.precioCOP ??= resultados.priceCOP; resultados.priceCOP ??= resultados.precioCOP;
    resultados.trmUsada ??= resultados.exchangeRateUsed; resultados.exchangeRateUsed ??= resultados.trmUsada;
    resultados.totalConDescuentoCOP ??= resultados.totalWithDiscountCOP; resultados.totalWithDiscountCOP ??= resultados.totalConDescuentoCOP;
    resultados.estadoAprobacion ??= resultados.approvalStatus; resultados.approvalStatus ??= resultados.estadoAprobacion;
  }
}

/** Normalize quote POST/PUT bodies: top-level EN/ES pairs + nested JSON subfields. */
export function normalizeQuoteBody(body: Record<string, unknown>): Record<string, unknown> {
  const destinatario = (body.consignee ?? body.destinatario) as QuoteSubRow | undefined;
  const proveedor = (body.supplier ?? body.proveedor) as QuoteSubRow | undefined;
  const producto = (body.product ?? body.producto) as QuoteSubRow | undefined;
  const resultados = (body.results ?? body.resultados) as QuoteSubRow | undefined;
  const dimensiones = body.dimensions ?? body.dimensiones;

  normalizeQuoteSubfields(destinatario, proveedor, producto, resultados);

  const out: Record<string, unknown> = {
    ...body,
    consignee: destinatario,
    destinatario,
    supplier: proveedor,
    proveedor,
    product: producto,
    producto,
    results: resultados,
    resultados,
    dimensions: dimensiones,
    dimensiones,
  };

  for (const [en, es] of QUOTE_BILINGUAL_PAIRS) {
    const v = out[en] ?? out[es];
    if (v !== undefined) {
      out[en] = v;
      out[es] = v;
    }
  }

  return out;
}

/**
 * The quote form edits `dimensiones`, but a quote loaded for editing also carries the English
 * `dimensions` mirror, which `normalizeQuoteBody` prefers. Without this, a quote edited from 1
 * to N pieces was saved with the old single dimension row (PDF, client breakdown, operation).
 */
export function withEditedDimensions<T extends { dimensiones?: unknown; dimensions?: unknown }>(form: T): T {
  return form.dimensiones === undefined ? form : { ...form, dimensions: form.dimensiones };
}

export function quoteConsignee(q: Pick<QuoteData, 'consignee' | 'destinatario'> | null | undefined): QuoteConsignee | undefined {
  return q?.consignee ?? q?.destinatario;
}

export function quoteSupplier(q: Pick<QuoteData, 'supplier' | 'proveedor'> | null | undefined): QuoteSupplier | undefined {
  return q?.supplier ?? q?.proveedor;
}

export function quoteProduct(q: Pick<QuoteData, 'product' | 'producto'> | null | undefined): QuoteProduct | undefined {
  return q?.product ?? q?.producto;
}

export function quoteResults(q: Pick<QuoteData, 'results' | 'resultados'> | null | undefined): QuoteResults | undefined {
  return q?.results ?? q?.resultados;
}

/**
 * Quote number ("202608280001"). A `SELECT * FROM quotes` row only carries the English
 * `consecutive`; reading `consecutivo` there printed "Cotización #undefined" in notifications,
 * emails and WhatsApp templates (T-18c). API-mapped quotes carry both keys.
 */
export function quoteNumberOf(q: { consecutive?: unknown; consecutivo?: unknown } | null | undefined): string {
  return String(q?.consecutive ?? q?.consecutivo ?? '');
}

export function consigneeCompany(c: QuoteConsignee | null | undefined): string {
  return c?.company ?? c?.razonSocial ?? c?.businessName ?? '';
}

export function consigneeName(c: QuoteConsignee | null | undefined): string {
  return c?.name ?? c?.contacto ?? '';
}

export function consigneeAddress(c: QuoteConsignee | null | undefined): string {
  return c?.address ?? c?.direccionEntrega ?? '';
}

export function consigneeCity(c: QuoteConsignee | null | undefined): string {
  return c?.city ?? c?.ciudad ?? '';
}

export function consigneeCountry(c: QuoteConsignee | null | undefined): string {
  return c?.country ?? c?.pais ?? '';
}

export function consigneePostalCode(c: QuoteConsignee | null | undefined): string {
  return c?.postalCode ?? c?.codigoPostal ?? '';
}

export function supplierCompany(s: QuoteSupplier | null | undefined): string {
  return s?.company ?? s?.nombreCompania ?? '';
}

export function supplierName(s: QuoteSupplier | null | undefined): string {
  return s?.name ?? s?.personaContacto ?? '';
}

export function supplierAddress(s: QuoteSupplier | null | undefined): string {
  return s?.address ?? s?.direccion ?? '';
}

export function supplierPhone(s: QuoteSupplier | null | undefined): string {
  return s?.phone ?? s?.telefono ?? '';
}

export function supplierCity(s: QuoteSupplier | null | undefined): string {
  return s?.city ?? s?.ciudad ?? '';
}

export function supplierCountry(s: QuoteSupplier | null | undefined): string {
  return s?.country ?? s?.pais ?? '';
}

export function supplierPostalCode(s: QuoteSupplier | null | undefined): string {
  return s?.postalCode ?? s?.zipCode ?? '';
}

export function productName(p: QuoteProduct | null | undefined): string {
  return p?.name ?? p?.nombre ?? '';
}

export function productPackagingType(p: QuoteProduct | null | undefined): string {
  return p?.packagingType ?? p?.tipoEmpaque ?? '';
}

export function productDeclaredValue(p: QuoteProduct | null | undefined): number {
  return p?.declaredValue ?? p?.valorDeclarado ?? 0;
}

export function productPieceCount(p: QuoteProduct | null | undefined): number {
  return p?.pieceCount ?? p?.cantidadPiezas ?? 0;
}

export function productWeightsPerPiece(p: QuoteProduct | null | undefined): number[] {
  return p?.weightsPerPiece ?? p?.pesosPorPieza ?? [];
}

export interface QuotePieceRow {
  units: number;
  length: number | null;
  width: number | null;
  height: number | null;
  volumetricKg: number;
  physicalKg: number;
}

/**
 * One row per billed piece, pairing weightsPerPiece[i] with dimension row i exactly like the
 * quote engine does (`Math.max(weights, rows)` in pages/Quote.tsx). A quote saved with fewer
 * dimension rows than weights (pre-T-1 edit bug) still lists every piece it was billed for,
 * with unknown measurements instead of dropping the piece (PDF and quote details, Fase T-3).
 */
export function quotePieceRows(q: Pick<QuoteData, 'product' | 'producto' | 'dimensiones'> | null | undefined): QuotePieceRow[] {
  const weights = productWeightsPerPiece(quoteProduct(q));
  const raw = (q as any)?.dimensiones ?? (q as any)?.dimensions;
  const dims: any[] = Array.isArray(raw) ? raw : [];
  const count = Math.max(weights.length, dims.length);
  return Array.from({ length: count }, (_, i) => {
    const d = dims[i];
    return {
      units: Number(d?.unidades) || 1,
      length: d ? Number(d.length ?? d.largo) || 0 : null,
      width: d ? Number(d.width ?? d.ancho) || 0 : null,
      height: d ? Number(d.height ?? d.alto) || 0 : null,
      volumetricKg: Number(d?.volumetricWeight ?? d?.pesoVolumetrico) || 0,
      physicalKg: Number(weights[i]) || 0,
    };
  });
}

export function productWeightPerPiece(p: QuoteProduct | null | undefined): number {
  return p?.weightPerPiece ?? p?.pesoPorPieza ?? 0;
}

export function productQuoteWeight(p: QuoteProduct | null | undefined): number {
  return p?.quoteWeight ?? p?.pesoCotizacion ?? 0;
}

export function resultsPhysicalWeight(r: QuoteResults | null | undefined): number {
  return r?.totalPhysicalWeight ?? r?.pesoTotalFisico ?? 0;
}

export function resultsVolumetricWeight(r: QuoteResults | null | undefined): number {
  return r?.totalVolumetricWeight ?? r?.pesoTotalVolumetrico ?? 0;
}

export function resultsBillableWeight(r: QuoteResults | null | undefined): number {
  return r?.billableWeight ?? r?.pesoFacturable ?? 0;
}

export function resultsPriceUSD(r: QuoteResults | null | undefined): number {
  return r?.priceUSD ?? r?.precioUSD ?? 0;
}

export function resultsPriceCOP(r: QuoteResults | null | undefined): number {
  return r?.priceCOP ?? r?.precioCOP ?? 0;
}

export function resultsExchangeRate(r: QuoteResults | null | undefined): number {
  return r?.exchangeRateUsed ?? r?.trmUsada ?? 0;
}

export function resultsTotalWithDiscountCOP(r: QuoteResults | null | undefined): number | undefined {
  return r?.totalWithDiscountCOP ?? r?.totalConDescuentoCOP;
}

export function resultsApprovalStatus(r: QuoteResults | null | undefined) {
  return r?.approvalStatus ?? r?.estadoAprobacion;
}

export function resultsSentToClient(r: QuoteResults | null | undefined): boolean {
  return !!(r?.sentToClient ?? r?.enviadoCliente);
}

export function resultsInvitedToPay(r: QuoteResults | null | undefined): boolean {
  return !!(r?.invitedToPay ?? r?.invitadoAPagar);
}

export function resultsCollectionPaymentStatus(r: QuoteResults | null | undefined): string | undefined {
  return r?.collectionPaymentStatus ?? r?.estadoPagoRecaudo;
}

/**
 * Dimension row with the English keys the server reads (length/width/height/volumetricWeight),
 * keeping the Spanish ones the forms write (emptyQuoteDimension). T-16: the public request
 * endpoint validated English keys while /cotiza sent Spanish ones, so it rejected every request.
 * An English key that already came wins; a missing value is never invented.
 */
export function withEnglishDimensionKeys<T>(row: T): T {
  if (!row || typeof row !== 'object') return row;
  const d = row as Record<string, unknown>;
  const out: Record<string, unknown> = { ...d };
  for (const [en, es] of [['length', 'largo'], ['width', 'ancho'], ['height', 'alto'], ['volumetricWeight', 'pesoVolumetrico']] as const) {
    if (out[en] === undefined && d[es] !== undefined) out[en] = d[es];
  }
  return out as T;
}

export function emptyQuoteDimension(): { id: string; largo: number; ancho: number; alto: number; unidades: number; pesoVolumetrico: number } {
  return { id: crypto.randomUUID(), largo: 0, ancho: 0, alto: 0, unidades: 1, pesoVolumetrico: 0 };
}

/** Grow/shrink piece arrays without wiping existing weights or dimensions. */
export function resizeQuotePieceArrays(
  count: number,
  weightsPerPiece: number[] = [],
  dimensiones: Array<Record<string, unknown>> = [],
): { weightsPerPiece: number[]; dimensiones: Array<Record<string, unknown>>; quoteWeight: number } {
  const n = Math.max(1, Math.floor(Number(count)) || 1);
  const weights = Array.from({ length: n }, (_, i) => Number(weightsPerPiece[i]) || 0);
  const dims = Array.from({ length: n }, (_, i) => {
    const existing = dimensiones[i];
    if (existing && typeof existing === 'object') {
      return { ...existing };
    }
    return emptyQuoteDimension();
  });
  const quoteWeight = weights.reduce((acc, p) => acc + p, 0);
  return { weightsPerPiece: weights, dimensiones: dims, quoteWeight };
}

/** Empty consignee for new quote forms. */
export function emptyConsignee(): QuoteConsignee {
  return { name: '', company: '', address: '', city: '', postalCode: '', country: '', email: '', phone: '' };
}

/** Empty supplier for new quote forms. */
export function emptySupplier(): QuoteSupplier {
  return { email: '', company: '', name: '', address: '', phone: '', postalCode: '', city: '', country: '' };
}

/** Empty product for new quote forms. */
export function emptyProduct(): QuoteProduct {
  return { name: '', packagingType: '', declaredValue: 0, pieceCount: 1, weightsPerPiece: [0], weightPerPiece: 0, quoteWeight: 0 };
}
